import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import authLogo from '../layouts/images/incyia-logo-auth.png'
import './auth.css'

type AuthShellProps = {
  children: React.ReactNode
  foot?: React.ReactNode
  wide?: boolean
  tag?: string
}

export function AuthShell({ children, foot, wide, tag }: AuthShellProps) {
  const { t, i18n } = useTranslation()
  const [lang, setLang] = useState(
    (localStorage.getItem('i18nextLng') || i18n.language || 'en').slice(0, 2)
  )

  useEffect(() => {
    const html = document.documentElement
    html.classList.add('auth-no-zoom')
    return () => {
      html.classList.remove('auth-no-zoom')
    }
  }, [])

  const toggleLanguage = () => {
    const next = lang === 'en' ? 'fr' : 'en'
    setLang(next)
    i18n.changeLanguage(next)
    localStorage.setItem('i18nextLng', next)
  }

  return (
    <div className="incyia-auth-solo">
      <div className="incyia-auth-topbar">
        <button
          type="button"
          className="incyia-auth-top-btn"
          onClick={toggleLanguage}
          aria-label="Change language"
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            width="14"
            height="14"
            aria-hidden
          >
            <circle cx="8" cy="8" r="6.2" />
            <path d="M2 8h12M8 2c1.6 1.7 2.4 3.7 2.4 6S9.6 12.3 8 14c-1.6-1.7-2.4-3.7-2.4-6S6.4 3.7 8 2z" />
          </svg>
          {lang.toUpperCase()}
        </button>
      </div>

      <div className={`incyia-auth-stage${wide ? ' is-wide' : ''}`}>
        <div className={`incyia-auth-card${wide ? ' is-wide' : ''}`}>
          <div className="incyia-auth-mark">
            <img className="incyia-auth-logo" src={authLogo} alt="INCYIA" />
          </div>
          {children}
        </div>

        <div className="incyia-auth-below">
          {foot ? <div className="incyia-auth-foot">{foot}</div> : null}
          <div className="incyia-auth-tag">
            {tag || t('auth.tagline', 'Governance, risk and compliance')}
          </div>
        </div>
      </div>
    </div>
  )
}

type AuthPasswordProps = {
  id?: string
  name?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  autoComplete?: string
  required?: boolean
}

export function AuthPasswordInput({
  id,
  name,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
}: AuthPasswordProps) {
  const [show, setShow] = useState(false)
  return (
    <span className="incyia-auth-pw-wrap">
      <input
        id={id}
        className="incyia-auth-inp"
        type={show ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
      />
      <button
        type="button"
        className="incyia-auth-pw-eye"
        aria-label={show ? 'Hide password' : 'Show password'}
        onClick={() => setShow((s) => !s)}
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden
        >
          <path d="M1.5 8S3.9 3.8 8 3.8 14.5 8 14.5 8 12.1 12.2 8 12.2 1.5 8 1.5 8z" />
          <circle cx="8" cy="8" r="1.9" />
        </svg>
      </button>
    </span>
  )
}

export function passwordStrength(password: string) {
  const rules = {
    len: password.length >= 12,
    up: /[a-z]/.test(password) && /[A-Z]/.test(password),
    num: /\d/.test(password),
    // Match Keycloak GRC realm specials
    sym: /[@$!%*?&]/.test(password),
  }
  const score = Object.values(rules).filter(Boolean).length
  return { rules, score }
}
